/**
 * Web Nova mascot — the SVG twin of the Skia `NovaMascot.tsx` (Skia needs a
 * CanvasKit bundle on web, which this app doesn't ship). Same geometry and
 * the same motion (`nova-mascot-motion.ts`), driven by Reanimated.
 *
 * Layers are stacked so each changing thing redraws alone: the body (with its
 * drop-shadow filter) never changes inside its SVG and moves by a view
 * transform, while the eyes, ring dots and bubble live in their own SVGs.
 */
import React, { memo, useId } from 'react';
import { StyleSheet, View, type ViewStyle } from 'react-native';
import Animated, {
  useAnimatedProps,
  useAnimatedStyle,
  type SharedValue,
} from 'react-native-reanimated';
import Svg, {
  Circle,
  ClipPath,
  Defs,
  Ellipse,
  FeDropShadow,
  FeGaussianBlur,
  Filter,
  G,
  LinearGradient,
  Path,
  RadialGradient,
  Rect,
  Stop,
} from 'react-native-svg';
import { useTheme } from '../../hooks/useTheme';
import {
  BUBBLE,
  BUBBLE_TRAIL,
  MASCOT_BODY_PATH,
  MASCOT_COLORS as C,
  MASCOT_UNITS,
  MASCOT_VIEWBOX,
  RING_DOTS,
} from './nova-mascot-geometry';
import { bodyPose, bubbleDot, eyePose, groundScale, isFarDot, ringDot } from './nova-mascot-motion';
import { useMascotDriver, type MascotDriver, type NovaMascotState } from './useMascotDriver';

export type { NovaMascotState } from './useMascotDriver';

const AnimatedCircle = Animated.createAnimatedComponent(Circle);
const AnimatedRect = Animated.createAnimatedComponent(Rect);

export interface NovaMascotProps {
  state: NovaMascotState;
  /** 0..1 voice level: the mic while listening, Nova's own playback while speaking. */
  level: SharedValue<number>;
  size?: number;
  style?: ViewStyle;
}

const FAR_DOTS = Array.from({ length: RING_DOTS }, (_, i) => i).filter((i) => isFarDot(i));
const NEAR_DOTS = Array.from({ length: RING_DOTS }, (_, i) => i).filter((i) => !isFarDot(i));

type Drive = MascotDriver & { level: SharedValue<number> };

function RingDot({ index, drive }: { index: number; drive: Drive }) {
  const props = useAnimatedProps(() => {
    const d = ringDot(index, drive.ringPhase.value, drive.level.value, drive.blends.value.listening);
    return { cx: d.cx, cy: d.cy, r: d.r, opacity: d.opacity };
  });
  return <AnimatedCircle animatedProps={props} fill="url(#novaRingDot)" />;
}

function RingLayer({ indices, size, drive }: { indices: number[]; size: number; drive: Drive }) {
  return (
    <Svg width={size} height={size} viewBox={MASCOT_VIEWBOX} style={StyleSheet.absoluteFill}>
      <Defs>
        <LinearGradient id="novaRingDot" gradientUnits="userSpaceOnUse" x1="-108" y1="-96" x2="112" y2="102">
          <Stop offset="0" stopColor={C.dotA} />
          <Stop offset="0.14" stopColor={C.dotB} />
          <Stop offset="0.58" stopColor={C.dotC} />
          <Stop offset="1" stopColor={C.dotD} />
        </LinearGradient>
      </Defs>
      {indices.map((i) => (
        <RingDot key={i} index={i} drive={drive} />
      ))}
    </Svg>
  );
}

function Eye({ which, drive }: { which: 0 | 1; drive: Drive }) {
  const props = useAnimatedProps(() => {
    const p = eyePose(which, drive.clock.value, drive.blends.value, drive.level.value);
    return { x: p.x, y: p.y, width: p.w, height: p.h, rx: p.rx, ry: p.ry };
  });
  return (
    <AnimatedRect
      animatedProps={props}
      fill="url(#novaEye)"
      stroke="#FFFFFF"
      strokeWidth={1.05}
      filter="url(#novaEyeShadow)"
    />
  );
}

function BubbleDot({ index, drive }: { index: number; drive: Drive }) {
  const props = useAnimatedProps(() => {
    const d = bubbleDot(index, drive.clock.value);
    return { cy: d.cy, r: d.r, opacity: d.opacity };
  });
  return <AnimatedCircle animatedProps={props} cx={(index - 1) * BUBBLE.dotGap} fill={C.bubbleDot} />;
}

export const NovaMascot = memo(function NovaMascot({ state, level, size = 240, style }: NovaMascotProps) {
  const { isDark } = useTheme();
  const driver = useMascotDriver(state, level);
  const drive: Drive = { ...driver, level };
  const { clock, blends, shake } = driver;
  const clipId = `novaBody${useId().replace(/:/g, '')}`;
  const px = size / MASCOT_UNITS;

  const bodyStyle = useAnimatedStyle(() => {
    const b = bodyPose(clock.value, blends.value, level.value, shake.value);
    return {
      opacity: b.opacity,
      transform: [
        { translateX: b.tx * px },
        { translateY: b.ty * px },
        { scaleX: b.sx },
        { scaleY: b.sy },
      ],
    };
  });

  const groundStyle = useAnimatedStyle(() => ({
    opacity: 1 - blends.value.thinking * 0.35,
    transform: [{ scaleX: groundScale(clock.value, blends.value, level.value) }],
  }));

  const bubbleStyle = useAnimatedStyle(() => {
    const k = blends.value.thinking;
    const breath = 1 + Math.sin((clock.value * Math.PI * 2) / 2.4) * 0.012;
    return {
      opacity: k,
      transform: [{ translateY: (1 - k) * 12 * px }, { scale: (0.86 + 0.14 * k) * breath }],
    };
  });

  return (
    <View
      pointerEvents="none"
      accessible={false}
      importantForAccessibility="no-hide-descendants"
      style={[{ width: size, height: size }, style]}
    >
      <Animated.View style={[StyleSheet.absoluteFill, groundStyle]}>
        <Svg width={size} height={size} viewBox={MASCOT_VIEWBOX}>
          <Defs>
            <Filter id="novaGroundBlur" x="-40%" y="-120%" width="180%" height="340%">
              <FeGaussianBlur stdDeviation={7} />
            </Filter>
          </Defs>
          <Ellipse cx={0} cy={102} rx={72} ry={12} fill={C.ground} opacity={isDark ? 0.45 : 0.3} filter="url(#novaGroundBlur)" />
        </Svg>
      </Animated.View>

      <RingLayer indices={FAR_DOTS} size={size} drive={drive} />

      <Animated.View style={[StyleSheet.absoluteFill, bodyStyle]}>
        {/* Static body: its filters are drawn once, the view does the moving. */}
        <Svg width={size} height={size} viewBox={MASCOT_VIEWBOX} style={StyleSheet.absoluteFill}>
          <Defs>
            <LinearGradient id="novaBodyFill" gradientUnits="userSpaceOnUse" x1="0" y1="-112" x2="0" y2="108">
              <Stop offset="0" stopColor={C.bodyTop} />
              <Stop offset="0.41" stopColor={C.bodyMid} />
              <Stop offset="1" stopColor={C.bodyBottom} />
            </LinearGradient>
            {/* The reference's belly glow: a round gradient squashed 1.25 × 0.5. */}
            <RadialGradient
              id="novaGlow"
              cx="0"
              cy="92"
              r="112"
              gradientUnits="userSpaceOnUse"
              gradientTransform="matrix(1.25 0 0 0.5 0 46)"
            >
              <Stop offset="0" stopColor={C.glow} stopOpacity={0.95} />
              <Stop offset="0.5" stopColor={C.glow} stopOpacity={0.4} />
              <Stop offset="1" stopColor={C.glow} stopOpacity={0} />
            </RadialGradient>
            <RadialGradient id="novaHighlight" cx="56" cy="-56" r="94" gradientUnits="userSpaceOnUse">
              <Stop offset="0" stopColor="#FFFFFF" stopOpacity={0.58} />
              <Stop offset="0.56" stopColor="#FFFFFF" stopOpacity={0.08} />
              <Stop offset="1" stopColor="#FFFFFF" stopOpacity={0} />
            </RadialGradient>
            <Filter id="novaBodyShadow" x="-35%" y="-32%" width="170%" height="190%">
              <FeDropShadow dx={0} dy={5} stdDeviation={7} floodColor="#5D4AAD" floodOpacity={0.2} />
            </Filter>
            <ClipPath id={clipId}>
              <Path d={MASCOT_BODY_PATH} />
            </ClipPath>
          </Defs>
          <Path d={MASCOT_BODY_PATH} fill="url(#novaBodyFill)" filter="url(#novaBodyShadow)" />
          <G clipPath={`url(#${clipId})`}>
            <Rect x={-125} y={-125} width={250} height={250} fill="url(#novaGlow)" />
            <Circle cx={56} cy={-56} r={94} fill="url(#novaHighlight)" />
          </G>
          <Path d={MASCOT_BODY_PATH} fill="none" stroke={C.outline} strokeWidth={1.69} strokeOpacity={0.76} />
        </Svg>
        {/* Eyes on their own layer, so a blink doesn't redraw the body. */}
        <Svg width={size} height={size} viewBox={MASCOT_VIEWBOX} style={StyleSheet.absoluteFill}>
          <Defs>
            <LinearGradient id="novaEye" x1="0" y1="0" x2="0" y2="1">
              <Stop offset="0.33" stopColor={C.eyeTop} />
              <Stop offset="1" stopColor={C.eyeBottom} />
            </LinearGradient>
            <Filter id="novaEyeShadow" x="-50%" y="-50%" width="200%" height="210%">
              <FeDropShadow dx={0} dy={4} stdDeviation={3.5} floodColor="#4F408E" floodOpacity={0.34} />
            </Filter>
          </Defs>
          <Eye which={0} drive={drive} />
          <Eye which={1} drive={drive} />
        </Svg>
      </Animated.View>

      <RingLayer indices={NEAR_DOTS} size={size} drive={drive} />

      <Animated.View style={[StyleSheet.absoluteFill, bubbleStyle]}>
        <Svg width={size} height={size} viewBox={MASCOT_VIEWBOX}>
          <Rect
            x={-BUBBLE.w / 2}
            y={BUBBLE.y - BUBBLE.h / 2}
            width={BUBBLE.w}
            height={BUBBLE.h}
            rx={BUBBLE.h / 2}
            fill={C.bubble}
          />
          {[0, 1, 2].map((i) => (
            <BubbleDot key={i} index={i} drive={drive} />
          ))}
          {BUBBLE_TRAIL.map((b) => (
            <Circle key={b.cx} cx={b.cx} cy={b.cy} r={b.r} fill={C.bubble} />
          ))}
        </Svg>
      </Animated.View>
    </View>
  );
});

export default NovaMascot;
